/**
 * InferenceBackendClient: runtime client for one service.
 * The JS counterpart of fastSDK's `FastClient`, Socaity/APIPod only.
 * Every endpoint call returns a `Job` immediately; results, streaming and
 * cancellation live on the job handle.
 *
 * ``SocaityClient`` remains as a compatibility alias.
 */

import { config } from '../../core/config';
import { HttpClient } from '../http';
import { Job } from '../../runtime/job';
import { JobRuntime } from '../../runtime/runtime';
import {
  EndpointDefinition,
  ServiceAddress,
  ServiceDefinition,
  isFileParameter,
  serviceAddressFromUrl,
} from '../../schemas/definitions';
import type { PriceEstimate } from '../../schemas/platform/pricing';

export type { PriceEstimate } from '../../schemas/platform/pricing';

export interface InferenceBackendClientOptions {
  /** Per-client API key; falls back to the global configuration. */
  apiKey?: string;
  /**
   * Service base address (URL string or ServiceAddress). Overrides the
   * definition's `service_address`; defaults to `config.baseUrl`.
   */
  address?: string | ServiceAddress;
  /** Socaity deployment id used by `estimate()`; defaults to the definition id. */
  deploymentId?: string;
}

/** Compatibility alias for InferenceBackendClientOptions. */
export type SocaityClientOptions = InferenceBackendClientOptions;

/** Inference gateway client for one service (submit / poll / stream / cancel). */
export class InferenceBackendClient {
  readonly definition: ServiceDefinition;
  readonly address: ServiceAddress;

  private readonly http: HttpClient;
  private readonly endpointsByKey = new Map<string, EndpointDefinition>();
  private readonly deploymentId?: string;

  constructor(definition: ServiceDefinition, options: InferenceBackendClientOptions = {}) {
    this.definition = definition;
    this.address = resolveAddress(definition, options);
    this.http = new HttpClient(options.apiKey);
    this.deploymentId = options.deploymentId ?? definition.id;

    for (const endpoint of definition.endpoints) {
      this.endpointsByKey.set(normalizeEndpointKey(endpoint.path), endpoint);
    }
  }

  get endpoints(): EndpointDefinition[] {
    return this.definition.endpoints;
  }

  /** Resolve an endpoint by path ('/text2voice', 'text2voice', 'text_2_voice' all match). */
  getEndpoint(path: string): EndpointDefinition {
    const endpoint = this.endpointsByKey.get(normalizeEndpointKey(path));
    if (!endpoint) {
      const available = [...this.endpointsByKey.values()].map((e) => e.path).join(', ');
      throw new Error(`Unknown endpoint '${path}'. Available endpoints: ${available}`);
    }
    return endpoint;
  }

  /**
   * Submit a job to an endpoint. Returns the job handle immediately:
   *
   *     const job = client.submitJob('/text2voice', { text: 'hello' });
   *     const audio = await job;             // assembled result
   *     for await (const chunk of await job.stream()) { ... }  // or live output
   */
  submitJob<T = any>(path: string, params: Record<string, any> = {}): Job<T> {
    const endpoint = this.getEndpoint(path);
    const job = new Job<T>(endpoint);
    job.runtime = new JobRuntime<T>(job, this.address, this.http, params);
    return job;
  }

  /** Submit and wait: sugar for `submitJob(path, params).getResult()`. */
  run<T = any>(path: string, params: Record<string, any> = {}): Promise<T> {
    return this.submitJob<T>(path, params).getResult();
  }

  /**
   * Re-attach to an already-submitted job by id (e.g. after a page reload)
   * using the Socaity gateway's job link convention. The returned handle
   * polls, streams and cancels like a freshly submitted job.
   */
  trackJob<T = any>(jobId: string, path?: string): Job<T> {
    const endpoint = path ? this.getEndpoint(path) : { path: '', parameters: [] };
    const job = new Job<T>(endpoint);
    job.runtime = new JobRuntime<T>(job, this.address, this.http, {}, {
      job_id: jobId,
      status: 'QUEUED',
      links: {
        status: `/gateway/v1/status/${jobId}`,
        cancel: `/gateway/v1/cancel/${jobId}`,
      },
    });
    return job;
  }

  /**
   * Estimate price & runtime before submitting. Sends only scalar features
   * (files are skipped) to the Socaity web backend. Anonymous access allowed.
   */
  async estimate(path: string, params: Record<string, any> = {}): Promise<PriceEstimate> {
    const endpoint = this.getEndpoint(path);
    const input: Record<string, any> = {};
    for (const param of endpoint.parameters) {
      if (isFileParameter(param)) continue;
      const value = params[param.name] ?? param.default;
      if (value === undefined || value === null || value === '') continue;
      input[param.name] = value;
    }

    const url = `${config.webBackendUrl.replace(/\/+$/, '')}/analytics/estimate`;
    const response = await this.http.request(url, {
      method: 'POST',
      query: { deployment_id: this.deploymentId, endpoint_id: endpoint.id ?? null },
      body: JSON.stringify(input),
      headers: { 'Content-Type': 'application/json' },
    });
    return (await response.json()) as PriceEstimate;
  }
}

/** Compatibility alias for InferenceBackendClient. */
export const SocaityClient = InferenceBackendClient;

function resolveAddress(definition: ServiceDefinition, options: InferenceBackendClientOptions): ServiceAddress {
  if (options.address) {
    return typeof options.address === 'string'
      ? serviceAddressFromUrl(options.address)
      : options.address;
  }
  if (definition.service_address) return definition.service_address;
  const idSegment = definition.id ? `/${definition.id}` : '';
  return serviceAddressFromUrl(`${config.baseUrl}${idSegment}`);
}

function normalizeEndpointKey(path: string): string {
  return path.replace(/^\/+|\/+$/g, '').toLowerCase().replace(/_/g, '-');
}
